import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useUserStore } from '../stores/useUser.store';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/customHooks/useAppContext';
import { Button } from '@/components/shadcn-ui/button';
import { FileText, HelpCircle, LogOut, Menu, Star, User, X, Zap } from 'lucide-react';
import { generateAuthUrl } from '@/network/project.service';
import { appAxios } from '@/network/base/appAxios';
import { toast } from 'sonner';

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, auth } = useUserStore();
  const { t, i18n } = useTranslation();
  const { appImages } = useAppContext();
  const [open, setOpen] = useState(false);
  const [helpMenuOpen, setHelpMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  const drawerRef = useRef(null);
  const helpMenuRef = useRef(null);
  const helpButtonRef = useRef(null);
  const profileMenuRef = useRef(null);
  const profileButtonRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/auth');
  };

  const handleLogin = async () => {
    const response = await appAxios.post('/Auth/v1.0/authurl', {
      type: 'login',
      grantType: 'email-password',
    });
    if (response.data.data.success && response.data.data.data?.authUrl) {
      window.location.href = response.data.data.data?.authUrl;
    } else {
      toast.error('Auth Url not received , please try again');
    }
  };

  const handleSignUp = async () => {
    const response = await appAxios.post('/Auth/v1.0/authurl', {
      type: 'signup',
      grantType: 'email-password',
    });
    if (response.data.data.success && response.data.data.data?.authUrl) {
      window.location.href = response.data.data.data?.authUrl;
    } else {
      toast.error('Auth Url not received , please try again');
    }
  };

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/counter', label: 'Counter' },
    { path: '/chat', label: 'Chat' },
  ];

  const handleOutsideClick = e => {
    if (drawerRef.current && !drawerRef.current.contains(e.target)) {
      setOpen(false);
    }
    if (
      helpMenuRef.current &&
      !helpMenuRef.current.contains(e.target) &&
      helpButtonRef.current &&
      !helpButtonRef.current.contains(e.target)
    ) {
      setHelpMenuOpen(false);
    }
    if (
      profileMenuRef.current &&
      !profileMenuRef.current.contains(e.target) &&
      profileButtonRef.current &&
      !profileButtonRef.current.contains(e.target)
    ) {
      setProfileMenuOpen(false);
    }
  };

  useEffect(() => {
    if (open || helpMenuOpen || profileMenuOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open, helpMenuOpen, profileMenuOpen]);

  return (
    <>
      <nav className="dark bg-black shadow-sm sticky top-0 z-50 font-baloo-2-thin">
        <div className="flex items-center justify-between p-4 mx-auto">
          <Link to={auth ? "/citypermit/project/new-chat" : "https://zermit.ai"} replace={auth ? false : true} className="flex-shrink-0">
            <div className="flex items-center gap-2">
              {/* <img src="/logo.svg" alt="Logo" className="cursor-pointer size-10" /> */}
              <span className="text-xl sm:text-2xl font-medium text-white font-montserrat tracking-widest uppercase mx-8">Zermit</span>
            </div>
          </Link>

          <button className="md:hidden p-2 text-foreground" onClick={() => setOpen(true)}>
            <Menu size={26} />
          </button>

          {/* <ul className="hidden lg:flex items-center gap-8 ml-24">
            <span className="text-base lg:text-xl font-baloo-2 text-white text-center">
              The fastest and cheapest way to pull a renovation permit
            </span>
          </ul> */}

          <div className="hidden md:flex items-center gap-[18px]">



            <Link
              to="https://zermit.ai/contractors"
              replace={true}
              target="_blank"
              className="flex items-center gap-1 text-white transition-colors text-sm font-medium underline underline-offset-2"
            >
              {/* <Zap className="w-4 h-4" />  */}
              <img src="/ProIcon.png" alt=" Find a Pro" className="w-4 h-4 mx-2" />
              Find a Pro
            </Link>



            <Link
              to="https://zermit.ai/businesses"
              className="flex items-center gap-1 text-white transition-colors text-sm font-medium underline underline-offset-2"
            >
              Business
            </Link>

            <Link
              to={auth ? "/citypermit/subscription" : "/subscription"}
              className="flex items-center gap-1 text-white transition-colors text-sm font-medium underline underline-offset-2"
            >
              Subscription
            </Link>

            {auth && (
              <Link
                to="https://zermit.ai"
                replace={true}
                target="_blank"
                className="flex items-center gap-1 text-white transition-colors text-sm font-medium underline underline-offset-2"
              >
                {/* <Zap className="w-4 h-4" />  */}
                About us
              </Link>
            )}

            {auth ? (
              <button
                ref={profileButtonRef}
                onClick={() => setProfileMenuOpen(prev => !prev)}
                className="w-9 h-9 rounded-full border border-[#515151] text-white flex items-center justify-center hover:bg-zinc-700 transition-colors"
                aria-label="Profile menu"
              >
                <User size={18} strokeWidth={1.5} />
              </button>
            ) : (
              <>
                <Button
                  onClick={handleLogin}
                  className="rounded-[20px] bg-black text-white border border-[#515151] px-6 py-2 hover:bg-black/80"
                >
                  Log in
                </Button>
                <Button
                  onClick={handleSignUp}
                  className="dark bg-secondary rounded-[20px] text-white border border-white/20 px-6 py-2 hover:bg-[#6A6A6A]"
                >
                  Sign Up
                </Button>
              </>
            )}

            {/* <button
              ref={helpButtonRef}
              onClick={() => setHelpMenuOpen(!helpMenuOpen)}
              className="w-8 h-8 rounded-full bg-transparent text-white/80 flex items-center justify-center hover:bg-white/10 hover:text-white transition-colors"
            >
              <HelpCircle className="size-10" strokeWidth={1.5} />
            </button> */}
          </div>
        </div>
      </nav>

      {/* Help Menu Dropdown - Positioned below navbar */}
      {helpMenuOpen && (
        <div
          ref={helpMenuRef}
          className="fixed right-4 z-40 w-64 bg-black rounded-lg shadow-xl border border-white/10 overflow-hidden"
          style={{ top: 'calc(4rem + 1rem)' }} // Navbar height + small gap
        >
          <Link
            to="/terms"
            className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/10 transition-colors"
            onClick={() => setHelpMenuOpen(false)}
          >
            <FileText className="w-5 h-5" />
            <span className="text-base">Terms & Policies</span>
          </Link>

          <div className="h-px bg-white/10" />

          {/* <Link
            to="/help"
            className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/10 transition-colors"
            onClick={() => setHelpMenuOpen(false)}
          >
            <HelpCircle className="w-5 h-5" />
            <span className="text-base">Help Center</span>
          </Link> */}
          <button
            type="button"
            className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/10 transition-colors w-full text-left"
            onClick={() => {
              setHelpMenuOpen(false);
              // openHelpModal(); or setHelpOpen(true)
            }}
          >
            <HelpCircle className="w-5 h-5" />
            <span className="text-base">Help Center</span>
          </button>

        </div>
      )}

      {/* Profile Dropdown */}
      {profileMenuOpen && auth && (
        <div
          ref={profileMenuRef}
          className="fixed right-4 z-40 w-52 bg-black rounded-2xl rounded-tr-none shadow-2xl border border-white/10 overflow-hidden"
          style={{ top: 'calc(4rem + 0.5rem)' }}
        >
          <Link
            to="#"
            className="flex items-center gap-3 px-4 py-3.5 text-white hover:bg-white/10 transition-colors"
            onClick={() => setProfileMenuOpen(false)}
          >
            {/* <span className="flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-yellow-400"> */}

            <Star size={13} className="text-pink-400 fill-pink-500" />
            {/* </span> */}
            <span className="text-sm font-medium">Pro Upgrade</span>
          </Link>

          <div className="h-px bg-white/10 mx-0" />

          <button
            type="button"
            className="flex items-center gap-3 px-4 py-3.5 text-white hover:bg-white/10 transition-colors w-full text-left"
            onClick={() => {
              setProfileMenuOpen(false);
              handleLogout();
            }}
          >
            <LogOut size={16} strokeWidth={1.5} className="text-white/80" />
            <span className="text-sm font-medium">Log Out</span>
          </button>
        </div >
      )}

      {/* Mobile Drawer Overlay */}
      {open && <div className="fixed inset-0 bg-black/50 z-40" />}

      {/* Mobile Drawer */}
      <div
        ref={drawerRef}
        className={`dark text-white fixed top-0 left-0 h-full w-72 bg-background shadow-lg border-r border-border z-50 transform transition-transform duration-300 ease-in-out ${open ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-xl font-semibold">Zermit AI</h2>
          <button onClick={() => setOpen(false)}>
            <X size={26} />
          </button>
        </div>

        <ul className="flex flex-col gap-4 p-4">
          <li className="flex flex-col gap-3">
            {auth ? (
              <div className="flex flex-col gap-1 rounded-2xl bg-zinc-900 border border-white/10 overflow-hidden">
                <Link
                  to="#"
                  className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/10 transition-colors"
                  onClick={() => setOpen(false)}
                >
                  <span className="flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-yellow-400">
                    <Star size={13} className="text-white fill-white" />
                  </span>
                  <span className="text-sm font-medium">Pro Upgrade</span>
                </Link>
                <div className="h-px bg-white/10" />
                <button
                  type="button"
                  className="flex items-center gap-3 px-4 py-3 text-white hover:bg-white/10 transition-colors w-full text-left"
                  onClick={() => {
                    setOpen(false);
                    handleLogout();
                  }}
                >
                  <LogOut size={16} strokeWidth={1.5} className="text-white/80" />
                  <span className="text-sm font-medium">Log Out</span>
                </button>
              </div>
            ) : (
              <>
                <Button
                  onClick={handleLogin}
                  className="w-full rounded-full bg-black text-white border border-white/20"
                >
                  Log in
                </Button>
                <Button
                  onClick={handleSignUp}
                  className="w-full rounded-full text-white border border-white/20"
                  style={{ backgroundColor: '#7B7B7B' }}
                >
                  Sign Up
                </Button>
              </>
            )}
          </li>

          <li className="flex flex-col gap-2 pt-2 border-t border-border">
            <Link
              to="https://zermit.ai/contractors"
              replace={true}
              target="_blank"
              className="flex items-center gap-2 text-foreground hover:text-primary transition-colors py-2"
              onClick={() => setOpen(false)}
            >
              <Zap className="w-5 h-5" />
              <span>Find a Pro</span>
            </Link>
            <Link
              to="/business"
              className="flex items-center gap-2 text-foreground hover:text-primary transition-colors py-2"
              onClick={() => setOpen(false)}
            >
              <span>Business</span>
            </Link>
            <Link
              to={auth ? "/citypermit/subscription" : "/subscription"}
              className="flex items-center gap-2 text-foreground hover:text-primary transition-colors py-2"
              onClick={() => setOpen(false)}
            >
              <span>Subscription</span>
            </Link>
          </li>

          <li className="flex flex-col gap-2 pt-2 border-t border-border">
            <Link
              to="/terms"
              className="flex items-center gap-2 text-foreground hover:text-primary transition-colors py-2"
              onClick={() => setOpen(false)}
            >
              <FileText className="w-5 h-5" />
              <span>Terms & Policies</span>
            </Link>
            <Link
              // to="/help"
              className="flex items-center gap-2 text-foreground hover:text-primary transition-colors py-2"
              onClick={() => setOpen(false)}
            >
              <HelpCircle className="w-5 h-5" />
              <span>Help Center</span>
            </Link>
          </li>
        </ul>
      </div>
    </>
  );
};

export default Navbar;